import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  SvgIconButton,
  Typography,
  InputField,
  Button,
  Grid,
  MuiBox,
  textSize,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { NumPad } from 'components/NumPad';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { selectors as authSelector, actions as authActions } from 'services/auth';
import { selectors as cartSelectors, actions as cartActions } from 'services/cart';
import { selectors as kioskSelector, Kiosk } from 'services/kiosk';
import { useKeyboard } from 'hooks/useKeyboard';
import { useStyles } from './styled';
import { selectors as configSelectors } from 'services/configuration';

const MaintSignInView: React.FC = () => {
  const [pinCode, setPinCode] = useKeyboard('');

  const { maintenanceLogin, resetMaintenanceMessage } = useActions({
    maintenanceLogin: authActions.maintenanceLogin,
    resetMaintenanceMessage: authActions.resetMaintenanceMessage
  });
  const { releaseCart } = useActions({ releaseCart: cartActions.releaseCart });
  const navigate = useNavigate();
  const maintenanceToken = useSelector(authSelector.selectMaintenanceId);
  const error = useSelector(authSelector.selectMaintenanceError);
  const kiosk = useSelector(kioskSelector.selectKiosk) as Kiosk;
  const isLoading = useSelector(authSelector.selectLoading);
  const cart = useSelector(cartSelectors.selectCart);
  const classes = useStyles();
  const config = useSelector(configSelectors.selectConfiguration);

  const pinOpacity = pinCode.length < 1 ? 0 : 1;
  const pinClearPointer = pinCode.length < 1 ? 'none' : 'auto';
  const transformTransition = 'opacity 250ms linear';

  const handleSubmit = () => {
    if (cart) {
      releaseCart(cart.releaseParams);
    }
    maintenanceLogin({ pinCode });
  };

  const handleExit = () => {
    clearInterval(sessionTimer.current);
    document.removeEventListener('click', resetTimer, true);
    resetMaintenanceMessage();
    navigate(-1);
  };

  const sessionTimeout = React.useRef(0);
  const sessionTimer = React.useRef<any>();
  const maintenanceLoginTimerCount =
    config && config.configuration && config.configuration.maintenanceLoginTimeout
      ? config.configuration.maintenanceLoginTimeout * 1000
      : 10000;

  React.useEffect(() => {
    document.addEventListener('click', resetTimer, true);
    sessionTimer.current = setInterval(() => {
      sessionTimeout.current = sessionTimeout.current + 1000;
      if (maintenanceLoginTimerCount === sessionTimeout.current) {
        handleExit();
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const resetTimer = () => {
    sessionTimeout.current = 0;
  };

  if (maintenanceToken) {
    clearInterval(sessionTimer.current);
    return <Navigate to="/maintenance" />;
  }
  if (!kiosk.setupComplete) return <Navigate to="/systems" />;

  return (
    <Grid container item direction="row" className={classes.maintSigninContainer} xl={6} lg={6} md={12}>
      {/* Sign In */}
      <Grid className={classes.maintSigninInputButtonBlock}>
        {/* Title */}
        <MuiBox className={classes.maintSigninHeading}>
          <Typography variant="h2" align="center" fontSize={64}>
            Maintenance <p>Center Login</p>
          </Typography>
        </MuiBox>
        {/* Number Input */}
        <MuiBox className={classes.maintSigninPinInput}>
          <InputField
            readOnly
            autoFocus
            fullWidth
            size="large"
            align="center"
            type="password"
            name="password"
            placeholder="Your Pin"
            value={pinCode}
            endIcon={
              <SvgIconButton
                appearance="transparent"
                onClick={() => {
                  setPinCode('clear');
                  resetMaintenanceMessage();
                }}
                sx={{
                  boxShadow: 'none',
                  opacity: pinOpacity,
                  pointerEvents: pinClearPointer
                }}
                customTransition={transformTransition}
              >
                <SvgIcon name="exit_circle" size={50} />
              </SvgIconButton>
            }
          />
          {/* Error message */}
          {error && (
            <Box height={30} style={{ marginBottom: '-50px', marginTop: '20px' }}>
              <Typography data-testid="errorMessage" align="center" color={color.RED} fontSize={textSize.kiosk.SMALL}>
                {error}
              </Typography>
            </Box>
          )}
        </MuiBox>
        {/* Sign In Button */}
        <Box margin="6 0" flexOverride="none">
          <Button
            fullWidth
            customSize="x-large"
            endIconName={isLoading ? undefined : 'next_circle'}
            appearance="primary"
            onClick={handleSubmit}
            disabled={pinCode === ''}
            isLoading={isLoading}
          >
            Sign In
          </Button>
        </Box>
        {/* Exit to Sales Button */}
        <Box flexOverride="none">
          <Button customSize="x-large" fullWidth appearance="white" onClick={handleExit}>
            Exit To Sales
          </Button>
        </Box>
      </Grid>
      {/* Numpad */}
      <Grid className={classes.maintSigninNumpadBlock}>
        <MuiBox>
          <NumPad
            onClick={(_, v: string) => {
              setPinCode(v);
              resetMaintenanceMessage();
            }}
          />
        </MuiBox>
      </Grid>
    </Grid>
  );
};

export default MaintSignInView;
